import React, { useState, useEffect } from "react"
import { X, RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, Search, ArrowDown, ChevronDown, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DatePickerWithRange } from "@/components/date-range-picker"
import { 
  PaginationNext, 
  PaginationPrevious
} from "@/components/ui/pagination"
import TaskTable from "./TaskTable"

const WorkflowHistoryPanel = ({
  onClose,
  workflowHistory,
  pagination,
  loading,
  error,
  onRefresh,
  users = [],
  csrf_token,
  workflowId,
  categories = [],
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined })
  const [selectedItem, setSelectedItem] = useState(null)
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [dataSearchTerm, setDataSearchTerm] = useState("")
  const [logsSearchTerm, setLogsSearchTerm] = useState("")
  const [instanceTasks, setInstanceTasks] = useState({})
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [expandedLogs, setExpandedLogs] = useState(new Set())

  const getStatusIcon = (status, errorMessage) => {
    if (status === 'completed' && errorMessage) {
      return <AlertCircle className="h-4 w-4 text-yellow-500" />
    }

    switch (status) {
      case 'completed':
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'running':
        return <Clock className="h-4 w-4 text-blue-500" />
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status, errorMessage) => {
    if (status === 'completed' && errorMessage) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200">⚠ Completed with warnings</Badge>
    }

    switch (status) {
      case 'completed':
      case 'success':
        return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200 hover:bg-green-200">✅ Success</Badge>
      case 'failed':
        return <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200 hover:bg-red-200">❌ Failed</Badge>
      case 'running':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200">⏳ Running</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    
    try {
      const date = new Date(dateString)
      
      // Check if the date is valid
      if (isNaN(date.getTime())) return 'Invalid Date'
      
      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
        timeZone: userTimezone
      })
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateString)
      return 'Invalid Date'
    }
  }

  const getDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A'
    const start = new Date(startTime)
    const end = new Date(endTime)
    const diffMs = end - start
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m ${diffSeconds % 60}s`
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ${diffSeconds % 60}s`
    } else {
      return `${diffSeconds}s`
    }
  }

  const scrollToSection = (sectionId, count) => {
    const element = document.getElementById(sectionId)
    if (element && count > 0) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      })
    }
  }

  const toggleLogExpansion = (logIndex) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev)
      if (newSet.has(logIndex)) {
        newSet.delete(logIndex)
      } else {
        newSet.add(logIndex)
      }
      return newSet
    })
  }

  // Handle search with debouncing
  const handleSearchChange = (value) => {
    setSearchTerm(value)
    setCurrentPage(1) // Reset to first page when searching
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      onRefresh({ search: value, status: statusFilter, dateRange, page: 1 })
    }, 500)
    
    setSearchTimeout(timeout)
  }

  // Handle status filter change
  const handleStatusChange = (value) => {
    setStatusFilter(value)
    setCurrentPage(1) // Reset to first page when filtering
    onRefresh({ search: searchTerm, status: value, dateRange, page: 1 })
  }

  // Handle date range change
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange)
    setCurrentPage(1) // Reset to first page when filtering
    onRefresh({ search: searchTerm, status: statusFilter, dateRange: newDateRange, page: 1 })
  }

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page)
    onRefresh({ search: searchTerm, status: statusFilter, dateRange, page })
  }

  // Handle refresh
  const handleRefresh = () => {
    onRefresh({ search: searchTerm, status: statusFilter, dateRange, page: currentPage })
  }

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
    }
  }, [searchTimeout])


  const viewDetails = async (item) => {
    setSelectedItem(item)
    setDataSearchTerm("")
    setLogsSearchTerm("")
    setExpandedLogs(new Set()) // Reset expanded logs when opening new item
    
    // Fetch detailed data for this instance if not already loaded
    if (!item.data && !item.logs && !item.skipped_tasks) {
      await fetchInstanceDetails(item.id)
    }
    
    // Fetch tasks for this instance if not already loaded
    if (!instanceTasks[item.id]) {
      fetchInstanceTasks(item.id)
    }
  }

  const fetchInstanceDetails = async (instanceId) => {
    if (!instanceId) return
    
    setLoadingDetails(true)
    try {
      if (!workflowId) {
        console.error('No workflow ID available')
        return
      }
      
      const response = await fetch(`/workflows/${workflowId}/instances/${instanceId}/details`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
          'Accept': 'application/json',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch instance details: ${response.status}`)
      }
      
      const data = await response.json()
      if (data.success && data.instance) {
        // Update the selected item with the detailed data
        setSelectedItem(prev => ({
          ...prev,
          ...data.instance
        }))
      }
    } catch (error) {
      console.error('Error fetching instance details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }

  const fetchInstanceTasks = async (instanceId) => {
    if (!instanceId) return
    
    setLoadingTasks(true)
    try {
      if (!workflowId) {
        console.error('No workflow ID available')
        return
      }
      
      const response = await fetch(`/workflows/${workflowId}/instances/${instanceId}/tasks`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
          'Accept': 'application/json',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.status}`)
      }
      
      const data = await response.json()
      setInstanceTasks(prev => ({
        ...prev,
        [instanceId]: {
          created_tasks: data.created_tasks || [],
          skipped_tasks: data.skipped_tasks || []
        }
      }))
    } catch (error) {
      console.error('Error fetching instance tasks:', error)
    } finally {
      setLoadingTasks(false)
    }
  }

  // Filter logs based on search term
  const filterLogs = (logs, searchTerm) => {
    if (!searchTerm) return logs
    return logs.filter(log => 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.level.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.timestamp.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  // Filter data based on search term
  const filterData = (data, searchTerm) => {
    if (!searchTerm || !data) return data
    const searchLower = searchTerm.toLowerCase()
    const dataString = JSON.stringify(data).toLowerCase()
    return dataString.includes(searchLower) ? data : null
  }

  // Highlight matching text in data
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm) return text
    const regex = new RegExp(`(${searchTerm})`, 'gi')
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800">$1</mark>')
  }

  return (
    <div className={`workflow-history-panel dark:bg-card border-l dark:border-l-[#1f2937] dark:border-r dark:border-r-[#1f2937] ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b dark:border-border">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Workflow History</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Filters */}
      <div className="p-4 border-b dark:border-border space-y-3">
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="completed">✅ Success</SelectItem>
              <SelectItem value="failed">❌ Failed</SelectItem>
              <SelectItem value="running">⏳ Running</SelectItem>
            </SelectContent>
          </Select>
          <DatePickerWithRange
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            className="flex-1"
            buttonClassName="h-10"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading history...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600 mb-2">Failed to load history</p>
              <p className="text-xs text-muted-foreground">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : workflowHistory.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No execution history found</p>
              <p className="text-xs text-muted-foreground">Run your workflow to see execution history here</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-auto">
              <div className="p-4 space-y-4">
                {workflowHistory.map((execution, index) => (
                  <div key={execution.id} className="border rounded-lg bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm">
                    {/* Header Section */}
                    <div 
                      className="p-4 pb-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => viewDetails(execution)}
                    >
                      <div className="flex items-center w-full justify-between mb-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(execution.status, execution.error_message)}
                          <span className="font-semibold text-base text-gray-900 dark:text-gray-100">
                            Execution #{execution.id}
                          </span>
                        </div>
                        {getStatusBadge(execution.status, execution.error_message)}
                      </div>
                      
                      {/* Main Info Grid - Reorganized for better spacing */}
                      <div className="grid grid-cols-1 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Triggered by:</span>
                          <span className="text-gray-900 dark:text-gray-100">{execution.triggered_by || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Duration:</span>
                          <span className="text-gray-900 dark:text-gray-100">{getDuration(execution.created_at, execution.finished_at)}</span>
                        </div>
                      </div>

                      {/* Error Message */}
                      {execution.error_message && (
                        <div className="mt-3">
                          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                            <div className="flex items-start gap-3">
                              <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Error Details</p>
                                <p className="text-sm text-red-700 dark:text-red-300 font-mono break-words leading-relaxed">
                                  {execution.error_message}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                ))}
              </div>
            </div>
            
            {/* Pagination */}
            {pagination && pagination.total > 0 && (
              <div className="border-t dark:border-border p-4">
                <div className="flex flex-col space-y-3">
                  <div className="text-xs text-muted-foreground text-center px-2">
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total.toLocaleString()} results
                  </div>
                  
                  <div className="flex items-center justify-between px-2">
                    <PaginationPrevious 
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      className={`text-xs px-2 py-1 ${pagination.current_page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}`}
                    />
                    
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-medium bg-muted px-2 py-1 rounded">
                        {pagination.current_page} / {pagination.last_page}
                      </span>
                    </div>
                    
                    <PaginationNext 
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      className={`text-xs px-2 py-1 ${pagination.current_page >= pagination.last_page ? "pointer-events-none opacity-50" : "cursor-pointer"}`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detailed View Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{selectedItem.identifier} Instance #{selectedItem.id}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedItem(null)}
                className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-auto">
              {loadingDetails ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 animate-spin text-indigo-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Loading Execution Details</h3>
                    <p className="text-sm text-muted-foreground">Fetching detailed data for this workflow instance...</p>
                  </div>
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* Basic Info */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                        {getStatusBadge(selectedItem.status, selectedItem.error_message)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Triggered by:</span>
                        <span className="text-gray-900 dark:text-gray-100">{selectedItem.triggered_by}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Trigger:</span>
                        <span className="text-gray-900 dark:text-gray-100">{selectedItem.trigger_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Tasks created:</span>
                        <button 
                          onClick={() => scrollToSection('created-tasks', selectedItem.tasks_count)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium underline cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20 px-1 py-0.5 rounded flex items-center gap-1"
                          title="Click to scroll to created tasks"
                        >
                          {selectedItem.tasks_count}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Started:</span>
                        <span className="text-gray-900 dark:text-gray-100">{formatDateTime(selectedItem.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Finished:</span>
                        <span className="text-gray-900 dark:text-gray-100">{selectedItem.finished_at ? formatDateTime(selectedItem.finished_at) : 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Duration:</span>
                        <span className="text-gray-900 dark:text-gray-100">{getDuration(selectedItem.created_at, selectedItem.finished_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Skipped tasks:</span>
                        <button 
                          onClick={() => scrollToSection('skipped-tasks', instanceTasks[selectedItem.id]?.skipped_tasks?.length)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium underline cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20 px-1 py-0.5 rounded flex items-center gap-1"
                          title="Click to scroll to skipped tasks"
                        >
                          {instanceTasks[selectedItem.id]?.skipped_tasks?.length || 0}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {selectedItem.error_message && (
                  <div className={`rounded-lg p-4 ${
                    selectedItem.status === 'completed'
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                      : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  }`}>
                    <h4 className={`text-lg font-semibold mb-3 ${
                      selectedItem.status === 'completed'
                        ? 'text-yellow-800 dark:text-yellow-200'
                        : 'text-red-800 dark:text-red-200'
                    }`}>
                      {selectedItem.status === 'completed' ? 'Warnings' : 'Error Details'}
                    </h4>
                    <div className={`bg-white dark:bg-gray-800 border rounded-md p-4 ${
                      selectedItem.status === 'completed'
                        ? 'border-yellow-200 dark:border-yellow-700'
                        : 'border-red-200 dark:border-red-700'
                    }`}>
                      <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                        {selectedItem.error_message}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Step Errors */}
                {selectedItem.data?.step_errors?.length > 0 && !loadingDetails && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
                      Step Issues ({selectedItem.data.step_errors.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedItem.data.step_errors.map((stepError, index) => (
                        <div key={index} className="bg-white dark:bg-gray-800 border border-yellow-200 dark:border-yellow-700 rounded-md p-3 text-sm">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {(stepError.step_type || 'step').replace(/_/g, ' ')}
                            {stepError.step_id ? ` #${stepError.step_id}` : ''}
                          </div>
                          <div className="text-gray-700 dark:text-gray-300 mt-1">
                            {stepError.error || stepError.reason || 'Unknown issue'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data */}
                {selectedItem.data && !loadingDetails && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Execution Data</h4>
                      <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search data..."
                          value={dataSearchTerm}
                          onChange={(e) => setDataSearchTerm(e.target.value)}
                          className="w-48 h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-4">
                      {filterData(selectedItem.data, dataSearchTerm) ? (
                        <pre 
                          className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words h-60 overflow-auto leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html: highlightSearchTerm(JSON.stringify(selectedItem.data, null, 2), dataSearchTerm)
                          }}
                        />
                      ) : (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                          No data matches your search term
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Logs */}
                {selectedItem.logs && selectedItem.logs.length > 0 && !loadingDetails && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Execution Logs ({selectedItem.logs.length} entries)</h4>
                      <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search logs..."
                          value={logsSearchTerm}
                          onChange={(e) => setLogsSearchTerm(e.target.value)}
                          className="w-48 h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-4">
                      <div className="space-y-3 h-60 overflow-auto">
                        {filterLogs(selectedItem.logs, logsSearchTerm).length > 0 ? (
                          filterLogs(selectedItem.logs, logsSearchTerm).map((log, index) => {
                            const isExpanded = expandedLogs.has(index)
                            const hasContext = log.context && Object.keys(log.context).length > 0
                            
                            return (
                              <div key={index} className="text-sm font-mono border-b border-gray-100 dark:border-gray-700 pb-2 last:border-b-0">
                                <div className="flex items-start gap-3">
                                  <span className="text-gray-500 dark:text-gray-400 text-xs font-medium min-w-[120px]">[{log.timestamp}]</span>
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    log.level === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                    log.level === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                    log.level === 'info' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                  }`}>
                                    {log.level.toUpperCase()}
                                  </span>
                                </div>
                                <div className="mt-1 text-gray-700 dark:text-gray-300 leading-relaxed">
                                  <div 
                                    className={`font-medium ${hasContext ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors' : ''}`}
                                    onClick={hasContext ? () => toggleLogExpansion(index) : undefined}
                                  >
                                    <div className="flex items-center gap-2">
                                      {hasContext && (
                                        isExpanded ? (
                                          <ChevronDown className="h-3 w-3 text-gray-400" />
                                        ) : (
                                          <ChevronRight className="h-3 w-3 text-gray-400" />
                                        )
                                      )}
                                      <span>{log.message}</span>
                                    </div>
                                  </div>
                                  {isExpanded && hasContext && (
                                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-1 pl-5">
                                      {Object.entries(log.context).map(([key, value]) => (
                                        <div key={key} className="flex">
                                          <span className="font-medium min-w-[120px]">{key}:</span>
                                          <span className="ml-2 break-all">
                                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })
                        ) : (
                          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                            No logs match your search term
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Created Tasks */}
                <div id="created-tasks">
                  {(() => {
                    const tasks = instanceTasks[selectedItem.id]?.created_tasks || []
                    return tasks.length > 0 ? (
                       <TaskTable
                         tasks={tasks}
                         title="Created Tasks"
                         emptyMessage="No tasks were created in this execution"
                         csrf_token={csrf_token}
                         users={users}
                         categories={categories}
                       />
                    ) : loadingTasks ? (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Created Tasks</h4>
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                          Loading tasks...
                        </div>
                      </div>
                    ) : null
                  })()}
                </div>

                {/* Skipped Tasks */}
                <div id="skipped-tasks">
                  {(() => {
                    const tasks = instanceTasks[selectedItem.id]?.skipped_tasks || []
                    return tasks.length > 0 ? (
                       <TaskTable
                         tasks={tasks}
                         title="Skipped Tasks (Already Existed)"
                         emptyMessage="No tasks were skipped in this execution"
                         csrf_token={csrf_token}
                         users={users}
                         categories={categories}
                       />
                    ) : loadingTasks ? (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Skipped Tasks</h4>
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                          Loading tasks...
                        </div>
                      </div>
                    ) : null
                  })()}
                </div>

                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WorkflowHistoryPanel
